/*
 *  This file hrone-dialog.component.ts is part of HROne. The intellectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  contentChild,
  inject,
  input,
  model,
  numberAttribute,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgStyle, NgTemplateOutlet } from '@angular/common';

export type THroneDialogPosition = 'center' | 'top' | 'bottom' | 'left' | 'right';

export type THroneDialogSize = 'x-small' | 'small' | 'medium' | 'large' | 'x-large';

@Component({
  selector: 'hrone-dialog',
  imports: [NgStyle, NgTemplateOutlet],
  templateUrl: './hrone-dialog.component.html',
  styleUrls: ['./hrone-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
    '(document:keydown.tab)': 'onTabKey($event)',
  },
})
export class HroneDialogComponent implements OnChanges, AfterViewChecked, OnDestroy {

  // ── Inputs ────────────────────────────────────────────────────────────────

  /** Controls dialog visibility. Use [(visible)] for two-way binding. */
  readonly visible = model(false);

  /** Header text. Ignored when a #header ng-template is projected. */
  readonly header = input('');

  /** Whether to render a backdrop overlay. `modal="modal"` / `modal="true"` are treated as true. */
  readonly modal = input(true, { transform: booleanAttribute });

  /** Whether to show the built-in close (×) button in the header. Opt-in — most dialogs render their own close/cancel. */
  readonly closable = input(false, { transform: booleanAttribute });

  /** Close the dialog when the Escape key is pressed. */
  readonly closeOnEscape = input(true, { transform: booleanAttribute });

  /** Close the dialog when the backdrop mask is clicked. */
  readonly dismissableMask = input(false, { transform: booleanAttribute });

  /** Allow the dialog to be dragged by its header. */
  readonly draggable = input(false, { transform: booleanAttribute });

  /** Where the dialog is positioned relative to the viewport. */
  readonly position = input<THroneDialogPosition>('center');

  /**
   * Panel width preset: x-small 400px, small 480px, medium 560px (default), large 720px, x-large 920px.
   * A width given via `style` takes precedence (inline style beats the size class).
   */
  readonly size = input<THroneDialogSize>('medium');

  /** Inline styles applied to the dialog panel (e.g. { width: '500px' }). Overrides `size`. */
  readonly style = input<Record<string, string>>({});

  /** Extra CSS class(es) applied to the dialog panel element. */
  readonly styleClass = input('');

  /** Extra CSS class(es) applied to the backdrop mask element — the main theming hook. */
  readonly maskStyleClass = input('');

  /** Base z-index of the mask layer. */
  readonly baseZIndex = input(1000, { transform: numberAttribute });

  /** Trap keyboard focus inside the dialog while it is open. */
  readonly focusTrap = input(false, { transform: booleanAttribute });

  /** Focus the first focusable element inside the dialog when it opens. */
  readonly focusOnShow = input(false, { transform: booleanAttribute });

  /** Show a maximize / restore button in the header. */
  readonly maximizable = input(false, { transform: booleanAttribute });

  /** Minimum Y coordinate (px from top of viewport) when dragging. */
  readonly minY = input(0, { transform: numberAttribute });

  /**
   * When 'body' (default), the mask is moved to document.body on open —
   * escaping any parent stacking context. Any other value keeps it inline.
   */
  readonly appendTo = input('body');

  /** Whether to show the dialog header section (title + icons row). */
  readonly showHeader = input(true, { transform: booleanAttribute });

  /** Whether to show the dialog footer section. */
  readonly showFooter = input(true, { transform: booleanAttribute });

  /** Prevent body from scrolling while the dialog is open (CSS-driven, see .scss). */
  readonly blockScroll = input(true, { transform: booleanAttribute });

  /** No-op — accepted so templates migrated from p-dialog keep compiling. Resize is not implemented. */
  readonly resizable = input(false, { transform: booleanAttribute });

  /** No-op — z-index is managed automatically. Accepted so templates migrated from p-dialog keep compiling. */
  readonly autoZIndex = input(true, { transform: booleanAttribute });

  // ── Outputs ───────────────────────────────────────────────────────────────

  /** Emitted after the dialog becomes visible. */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onShow = output<void>();

  /** Emitted after the dialog is hidden. */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  readonly onHide = output<void>();

  // ── Content slots ─────────────────────────────────────────────────────────

  /** Custom header template: <ng-template #header>…</ng-template> */
  protected readonly headerTemplate = contentChild<TemplateRef<unknown>>('header', { descendants: false });

  /** Custom footer template: <ng-template #footer>…</ng-template> */
  protected readonly footerTemplate = contentChild<TemplateRef<unknown>>('footer', { descendants: false });

  /**
   * Optional body template: <ng-template #content>…</ng-template>
   * Rendered after the default <ng-content /> slot (p-dialog order), which always renders.
   * descendants:false keeps a child component's own #content from being picked up.
   */
  protected readonly contentTemplate = contentChild<TemplateRef<unknown>>('content', { descendants: false });

  // ── View refs ─────────────────────────────────────────────────────────────

  private readonly _dialogEl = viewChild<ElementRef<HTMLElement>>('dialogEl');

  /** The mask div — moved to body when the dialog opens. */
  private readonly _maskEl = viewChild<ElementRef<HTMLElement>>('maskEl');

  // ── Internal state ────────────────────────────────────────────────────────

  protected readonly isMaximized = signal(false);

  /** Resolved z-index assigned when this dialog becomes visible. */
  protected readonly computedZIndex = signal(0);

  /** True while the leave transition is playing — keeps the element in the DOM during close. */
  protected readonly isClosing = signal(false);

  private static readonly CLOSE_ANIMATION_MS = 150;

  /** Set when visible becomes true; cleared in ngAfterViewChecked once the mask is rendered. */
  private _pendingAppend = false;

  /** id of close()'s pending cleanup setTimeout, so a reopen/external-close mid-animation can cancel it. */
  private _closeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private _dragOffset = { x: 0, y: 0 };
  private _dragStart = { mouseX: 0, mouseY: 0, offsetX: 0, offsetY: 0, naturalTop: 0 };
  private readonly _dragUnlisteners: (() => void)[] = [];

  // ── DI ───────────────────────────────────────────────────────────────────

  private readonly _renderer = inject(Renderer2);
  private readonly _hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // ── Computed ──────────────────────────────────────────────────────────────

  protected readonly maskClass = computed(() => {
    const classes = ['hrone-dialog-mask'];
    if (this.position() !== 'center') classes.push(`hrone-dialog-mask--${this.position()}`);
    if (!this.modal()) classes.push('hrone-dialog-mask--no-backdrop');
    if (!this.blockScroll()) classes.push('hrone-dialog-scrollable');
    if (this.appendTo() !== 'body') classes.push('hrone-dialog-mask--inline');
    if (this.maskStyleClass()) classes.push(...this.maskStyleClass().split(' ').filter(Boolean));
    return classes.join(' ');
  });

  protected readonly panelClass = computed(() => {
    const classes = ['hrone-dialog-panel', `hrone-dialog-panel--${this.size()}`];
    if (this.position() !== 'center') classes.push(`hrone-dialog-panel--position-${this.position()}`);
    if (this.styleClass()) classes.push(...this.styleClass().split(' ').filter(Boolean));
    if (this.isMaximized()) classes.push('hrone-dialog-panel--maximized');
    if (this.draggable()) classes.push('hrone-dialog-panel--draggable');
    return classes.join(' ');
  });

  protected readonly shouldRenderHeader = computed(() =>
    this.showHeader() && !!(this.headerTemplate() || this.header() || this.closable() || this.maximizable())
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    // Fires only for parent-driven changes — an internal close() sets the model directly.
    if (!changes['visible']) return;

    const isNowVisible = changes['visible'].currentValue === true;
    const wasVisible = changes['visible'].previousValue === true;

    if (isNowVisible && !wasVisible) {
      // Reopened while close()'s cleanup timer was still pending — cancel it so it
      // can't fire later and tear down this new open.
      this.cancelPendingClose();

      // Scan open overlays before this mask is appended, so it won't count itself.
      this.computedZIndex.set(this.getNextZIndex());

      this._dragOffset = { x: 0, y: 0 };
      this.isMaximized.set(false);

      // ngAfterViewChecked finishes the open once @if has rendered the mask.
      this._pendingAppend = true;
    } else if (!isNowVisible && wasVisible) {
      // computedZIndex === 0 means close() already ran cleanup — skip to avoid double-emit.
      if (this.computedZIndex() === 0) return;

      this.cancelPendingClose();

      // External close (parent set visible=false). Restore the mask to the host so
      // @if's view destruction removes it from document.body.
      this.restoreMaskToHost();
      this.computedZIndex.set(0);
      this.cleanupDragListeners();
      this.onHide.emit();
    }
  }

  ngAfterViewChecked(): void {
    const mask = this._maskEl()?.nativeElement;
    if (!this._pendingAppend || !mask) return;

    this._pendingAppend = false;
    // Only the mask moves to body — the host element stays in place.
    if (this.appendTo() === 'body') {
      document.body.appendChild(mask);
    }
    // Force a synchronous reflow so the "closed" starting state is committed before
    // the --active classes flip it — required for the CSS transition to run.
    // Must stay synchronous: an async gap here (rAF) caused orphaned masks on the drawer.
    // NOTE: must be a method call, not `void mask.offsetHeight` — Terser's
    // `pure_getters` (enabled when buildOptimizer is on, e.g. sanity-prod)
    // strips unused property reads, silently removing the reflow and making
    // the dialog appear instantly with no zoom/fade-in.
    mask.getBoundingClientRect();
    mask.classList.add('hrone-dialog-mask--active');
    mask.querySelector<HTMLElement>('.hrone-dialog-panel')?.classList.add('hrone-dialog-panel--active');
    this.applyDragTransform();
    if (this.focusOnShow()) {
      this.focusFirstElement();
    }
    this.onShow.emit();
  }

  ngOnDestroy(): void {
    this.cancelPendingClose();
    const mask = this._maskEl()?.nativeElement;
    // @if won't clean up a mask that lives on body, so remove it manually.
    if (mask?.parentElement === document.body) {
      document.body.removeChild(mask);
    }
    this.cleanupDragListeners();
  }

  // ── Keyboard handlers ─────────────────────────────────────────────────────

  protected onEscapeKey(): void {
    if (this.visible() && this.closeOnEscape()) {
      this.close();
    }
  }

  protected onTabKey(event: Event): void {
    if (!this.visible() || !this.focusTrap()) return;

    const e = event as KeyboardEvent;
    const focusable = this.getFocusableElements();
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // ── Public actions ────────────────────────────────────────────────────────

  /** Plays the leave transition, then sets visible=false and emits visibleChange + onHide. */
  close(): void {
    if (this.isClosing()) return;
    this.isClosing.set(true);
    // Drop the --active classes in the same tick so the --closing rules
    // unambiguously drive the leave transition.
    const openMask = this._maskEl()?.nativeElement;
    openMask?.classList.remove('hrone-dialog-mask--active');
    openMask?.querySelector<HTMLElement>('.hrone-dialog-panel')?.classList.remove('hrone-dialog-panel--active');
    this._closeTimeoutId = setTimeout(() => {
      this._closeTimeoutId = null;
      this.restoreMaskToHost();
      this.isClosing.set(false);
      this.computedZIndex.set(0);
      this.cleanupDragListeners();
      this.visible.set(false);
      this.onHide.emit();
    }, HroneDialogComponent.CLOSE_ANIMATION_MS);
  }

  protected onMaskClick(event: MouseEvent): void {
    if (this.dismissableMask() && event.target === this._maskEl()?.nativeElement) {
      this.close();
    }
  }

  protected toggleMaximize(): void {
    this.isMaximized.update(v => !v);
  }

  // ── Drag support ──────────────────────────────────────────────────────────

  protected onHeaderMouseDown(event: MouseEvent): void {
    const dialogEl = this._dialogEl()?.nativeElement;
    if (!this.draggable() || !dialogEl) return;
    event.preventDefault();

    // Disable the CSS transition so each drag-driven transform applies instantly.
    dialogEl.style.transition = 'none';
    const rect = dialogEl.getBoundingClientRect();
    const naturalTop = rect.top - this._dragOffset.y;

    this._dragStart = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      offsetX: this._dragOffset.x,
      offsetY: this._dragOffset.y,
      naturalTop,
    };

    this.cleanupDragListeners();

    const moveUn = this._renderer.listen('document', 'mousemove', (e: MouseEvent) => {
      const newX = this._dragStart.offsetX + (e.clientX - this._dragStart.mouseX);
      let newY = this._dragStart.offsetY + (e.clientY - this._dragStart.mouseY);

      if (this._dragStart.naturalTop + newY < this.minY()) {
        newY = this.minY() - this._dragStart.naturalTop;
      }

      this._dragOffset = { x: newX, y: newY };
      this.applyDragTransform();
    });

    const upUn = this._renderer.listen('document', 'mouseup', () => this.cleanupDragListeners());

    this._dragUnlisteners.push(moveUn, upUn);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private restoreMaskToHost(): void {
    const mask = this._maskEl()?.nativeElement;
    if (mask?.parentElement === document.body) {
      this._hostRef.nativeElement.appendChild(mask);
    }
  }

  private applyDragTransform(): void {
    const dialogEl = this._dialogEl()?.nativeElement;
    if (!dialogEl) return;
    const { x, y } = this._dragOffset;
    dialogEl.style.transform = `translate(${x}px, ${y}px)`;
  }

  private focusFirstElement(): void {
    this.getFocusableElements()[0]?.focus();
  }

  private getFocusableElements(): HTMLElement[] {
    const dialogEl = this._dialogEl()?.nativeElement;
    if (!dialogEl) return [];
    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    return Array.from(dialogEl.querySelectorAll<HTMLElement>(selector));
  }

  private cleanupDragListeners(): void {
    this._dragUnlisteners.forEach(fn => fn());
    this._dragUnlisteners.length = 0;
  }

  /**
   * Cancels close()'s pending cleanup timer, if any. Must run whenever the dialog is
   * reopened or externally closed mid-animation — otherwise the stale timer fires later
   * and force-closes / double-emits against the newer open/close cycle.
   */
  private cancelPendingClose(): void {
    if (this._closeTimeoutId !== null) {
      clearTimeout(this._closeTimeoutId);
      this._closeTimeoutId = null;
    }
    this.isClosing.set(false);
  }

  /**
   * Next z-index = highest z-index among open overlay masks (dialogs + drawers) + 10.
   * Shared DOM-based strategy — the same selector list is used by hrone-drawer,
   * hrone-popover and the other hrone-* overlays, so keep them in sync.
   */
  private getNextZIndex(): number {
    const overlays = document.querySelectorAll<HTMLElement>('.hrone-drawer-mask, .hrone-dialog-mask');
    let max = this.baseZIndex();
    overlays.forEach(el => {
      const z = parseInt(el.style.zIndex || '0', 10);
      if (z > max) max = z;
    });
    return max + 10;
  }
}
